/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { DegreePlan_LearnerAdmissionStatus as DegreePlanLearnerAdmissionStatus } from '__generated__/graphql-types';

import { Divider, LoadingSection, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import AdmissionDetailsButton from 'bundles/degree-home/components/v3-dpm/components/AdmissionDetailsButton';
import ErrorCard from 'bundles/degree-home/components/v3-dpm/components/ErrorCard';
import DegreeProgressBar from 'bundles/degree-home/components/v3-dpm/components/ProgressBar';
import ViewProgressButton from 'bundles/degree-home/components/v3-dpm/components/ViewProgressButton';
import { PATHS } from 'bundles/degree-home/components/v3-dpm/constants/constants';
import DisclaimersDialog from 'bundles/degree-home/components/v3-dpm/dialogs/DisclaimersDialog';
import type {
  UseCurriculum_DegreeCurriculumFragment as DegreeCurriculumFragment,
  UseCurriculum_GradeScaleFragment as GradeScaleFragment,
  UseCurriculum_PbaCurriculumFragment as PbaCurriculumFragment,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import {
  getGPAForSingleCurriculum,
  verifyDegreeCompletionStatus,
} from 'bundles/degree-home/components/v3-dpm/lib/gradeUtils';
import { getCurriculumCreditInfo } from 'bundles/degree-home/components/v3-dpm/lib/graduationUtils';
import { isDegreeCurriculum } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import useProgressionCalculator from 'bundles/degree-home/components/v3-dpm/lib/useProgressionCalculator';
import { Card } from 'bundles/program-loggedin-shared/components/Card';
import { renderGpa } from 'bundles/program-loggedin-shared/libraries/gradeUtils';

import _t from 'i18n!nls/degree-home';

type Mode = 'summary' | 'detail';

type Props = {
  variant?: Mode;
  degreeCurriculum: DegreeCurriculumFragment;
  isDegreeLearner: boolean;
  gradeScale: GradeScaleFragment;
};

type FooterProps = {
  minCredits: number;
  minGpa: number;
  gpa: number | null;
  variant: Mode;
  isDegreeLearner: boolean;
};

type ProgressCardContainerProps = {
  isDegreeLearner?: boolean;
  showTitle?: boolean;
};

const styles = {
  progressCardContainer: css`
    display: flex;
    flex-direction: column;
  `,

  progressCardTitle: css`
    margin-top: var(--cds-spacing-100);
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
    }
  `,

  footerContainer: css`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--cds-spacing-400);
    margin-top: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      flex-direction: column;
      gap: var(--cds-spacing-200);
    }
  `,
  gpaItemContainer: css`
    display: flex;
    flex-wrap: wrap;
    gap: var(--cds-spacing-400);

    ${breakpoints.down('md')} {
      gap: var(--cds-spacing-200);
    }

    ${breakpoints.down('xs')} {
      flex-direction: column;
      gap: var(--cds-spacing-100);
    }
  `,
  gpaItem: css`
    display: flex;
    gap: var(--cds-spacing-100);
    align-items: center;
    flex-direction: row;
    white-space: nowrap;
  `,

  infoContainer: css`
    display: flex;
    gap: var(--cds-spacing-50);
  `,

  footerButtons: css`
    margin-top: var(--cds-spacing-200);
    display: flex;
    gap: var(--cds-spacing-200);
  `,

  text: css`
    cursor: pointer;
  `,
};

const ProgressCardContainer: React.LegacyFunctionComponentWithChildren<ProgressCardContainerProps> = ({
  isDegreeLearner,
  showTitle,
  children,
}) => (
  <Card>
    <div css={styles.progressCardContainer}>
      {showTitle && (
        <Typography variant="h2semibold">
          {isDegreeLearner
            ? _t('Progress toward graduation')
            : _t('Get admitted to this degree to apply your earned credits')}
        </Typography>
      )}
      {children}
    </div>
  </Card>
);

export const LoadingComponent = () => (
  <ProgressCardContainer>
    <LoadingSection label={_t('Fetching data...')} />
  </ProgressCardContainer>
);

export const ErrorComponent = () => (
  <ProgressCardContainer>
    <ErrorCard hideBorder />
  </ProgressCardContainer>
);

const GpaItem = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <div css={styles.gpaItem}>
    <Typography2 component="span" color="supportText">
      {label}
    </Typography2>
    <Typography2 variant="subtitleMedium" component="span">
      {value}
    </Typography2>
  </div>
);

const Footer: React.FC<FooterProps> = ({ minCredits, minGpa, gpa, variant, isDegreeLearner }) => {
  const showLearnersGpa = typeof gpa === 'number';

  return (
    <div aria-label={_t('Degree progress details')} css={styles.footerContainer}>
      <div>
        <div css={styles.gpaItemContainer}>
          <GpaItem label={_t('Required credits')} value={minCredits} />
          <GpaItem label={_t('Required GPA')} value={renderGpa(minGpa)} />
          {showLearnersGpa && <GpaItem label={_t('Your GPA')} value={renderGpa(gpa)} />}
        </div>
        {variant === 'summary' && (
          <div css={styles.footerButtons}>
            <React.Fragment>
              <ViewProgressButton />
              {!isDegreeLearner && <AdmissionDetailsButton href={`./${PATHS.ADMISSIONS}`} />}
            </React.Fragment>
          </div>
        )}
      </div>
      {variant === 'detail' && (
        <div css={styles.infoContainer}>
          <DisclaimersDialog />
        </div>
      )}
    </div>
  );
};

export const ProgressCard = ({ degreeCurriculum, variant = 'summary', isDegreeLearner, gradeScale }: Props) => {
  const overallProgression = useProgressionCalculator();

  if (!degreeCurriculum || !overallProgression) {
    return null;
  }

  const { title, minCredits, minGpa } = degreeCurriculum;
  const {
    completed: completedCourseCreditValue,
    inProgress: inProgressCourseCreditValue,
    planned: plannedCourseCreditValue,
  } = getCurriculumCreditInfo(degreeCurriculum, overallProgression);

  const gpa = getGPAForSingleCurriculum(degreeCurriculum, gradeScale);

  const isDegreeComplete = verifyDegreeCompletionStatus(degreeCurriculum, overallProgression);
  const dptFooterVariant = variant === 'detail';

  return (
    <ProgressCardContainer isDegreeLearner={isDegreeLearner} showTitle>
      {title && (
        <Typography2 component="p" data-testid="degree-title-section" css={styles.progressCardTitle}>
          {title}
        </Typography2>
      )}
      <DegreeProgressBar
        credits={minCredits}
        completed={completedCourseCreditValue}
        inProgress={inProgressCourseCreditValue}
        planned={plannedCourseCreditValue}
        isDegreeComplete={isDegreeComplete}
        {...(dptFooterVariant
          ? {
              footer: (
                <Footer
                  minCredits={minCredits}
                  minGpa={minGpa}
                  gpa={gpa}
                  variant={variant}
                  isDegreeLearner={isDegreeLearner}
                />
              ),
            }
          : {})}
      />
      <div
        {...(dptFooterVariant
          ? {
              css: css`
                ${breakpoints.up('lg')} {
                  display: none;
                }
              `,
            }
          : {})}
      >
        <Divider />
        <Footer minCredits={minCredits} minGpa={minGpa} gpa={gpa} variant={variant} isDegreeLearner={isDegreeLearner} />
      </div>
    </ProgressCardContainer>
  );
};

const DegreeProgressCard = ({ variant }: { variant: Mode }) => {
  const curriculumGql = useCurriculum();

  const curriculum = curriculumGql.data?.Degree?.queryById?.curriculum;
  const curriculumsData: (DegreeCurriculumFragment | PbaCurriculumFragment)[] = curriculum?.curriculums || [];
  // the progress bar only looks at learner progress in the degree curriculum
  const degreeCurriculum = curriculumsData.find((curr) => isDegreeCurriculum(curr)) as
    DegreeCurriculumFragment | undefined;
  const learnerAdmissionStatus = curriculum?.learnerAdmissionStatus;
  // Based on the BE logic (see https://github.com/webedx-spark/degree-plan-application/blob/main/src/main/java/org/coursera/degreeplan/managers/LearnerAdmissionStatusManagerImpl.java)
  // of determining the learner's admission status, a "PBA" status also indicates that the learner has a normal degree association
  const isDegreeLearner =
    learnerAdmissionStatus === DegreePlanLearnerAdmissionStatus.Degree ||
    learnerAdmissionStatus === DegreePlanLearnerAdmissionStatus.Pba;

  const isLearnerNotEnrolled = learnerAdmissionStatus === DegreePlanLearnerAdmissionStatus.NotEnrolled;
  const gradeScale = curriculumGql.data?.Degree?.queryById?.gradeScale;
  if (curriculumGql.loading) {
    return <LoadingComponent />;
  }

  if (curriculumGql.error || isLearnerNotEnrolled) {
    return <ErrorComponent />;
  }

  if (!degreeCurriculum || !gradeScale) {
    return null;
  }

  return (
    <ProgressCard
      degreeCurriculum={degreeCurriculum}
      variant={variant}
      isDegreeLearner={isDegreeLearner}
      gradeScale={gradeScale}
    />
  );
};

export default DegreeProgressCard;
